import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
    "Event Marketing",
    "Performance Marketing",
    "CSR",
    "SEO and AEO",
    "KOL / UGC Marketing",
    "Social Media Management",
    "PR Services",
    "Content Strategy",
    "Affiliate Onboarding – VIP",
    "Funnel Building and Management",
    "Sponsorships and Branding",
    "Retention Strategies",
    "Full-service partnership"
];

const BUDGETS = [
    "< $25k / month",
    "$25k – $75k / month",
    "$75k – $250k / month",
    "$250k+ / month",
    "Project-based"
];

export default function Contact() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        service: "",
        budget: ""
    });
    const [loading, setLoading] = useState(false);

    const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Please fill your name and email.");
            return;
        }
        setLoading(true);
        try {
            // Backend still expects a `message` field; synthesise a concise one from
            // the remaining structured fields so the API contract is unchanged.
            const messageParts = [];
            if (form.service) messageParts.push(`Scaling: ${form.service}`);
            if (form.company) messageParts.push(`Company: ${form.company}`);
            if (form.budget) messageParts.push(`Budget: ${form.budget}`);
            const payload = {
                ...form,
                message: messageParts.join(" · ") || "Inbound enquiry via contact form."
            };
            await axios.post(`${API}/contact`, payload);
            setForm({ name: "", email: "", company: "", service: "", budget: "" });
            navigate("/thank-you");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong. Please email support@adxfusion.com.";
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <section className="container-x pt-24 pb-12">
                <p className="overline mb-8">Chapter 05 · Contact</p>
                <Reveal>
                    <h1
                        className="font-display leading-[0.9] tracking-tighter"
                        style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                    >
                        Let&apos;s talk{" "}
                        <span className="italic text-brand-primary">numbers.</span>
                    </h1>
                </Reveal>
                <Reveal delay={120} className="mt-10 max-w-3xl">
                    <p className="text-xl text-brand-frost/85 leading-relaxed">
                        Tell us about your firm, your market, and what you&apos;re trying to scale. A senior strategist will reply within one business day.
                    </p>
                </Reveal>
            </section>

            <section className="border-y border-brand-primary/10">
                <div className="container-x grid grid-cols-1 lg:grid-cols-12">
                    {/* INFO COLUMN */}
                    <div className="lg:col-span-5 py-16 lg:py-24 lg:pr-12 space-y-12 border-b lg:border-b-0 lg:border-r border-brand-primary/10">
                        <div>
                            <p className="overline mb-4">Direct</p>
                            <a href="mailto:support@adxfusion.com" className="font-display text-2xl sm:text-3xl text-brand-mist hover:text-brand-primary transition-colors break-all">
                                support@adxfusion.com
                            </a>
                        </div>
                        <div>
                            <p className="overline mb-4">For inquiries about</p>
                            <ul className="space-y-2 text-sm text-brand-primary/90">
                                <li>· New broker launches and market entry</li>
                                <li>· Trading firm growth strategy</li>
                                <li>· Fintech brand acquisition</li>
                                <li>· Sponsorship activation</li>
                                <li>· CRM and lifecycle architecture</li>
                            </ul>
                        </div>
                    </div>

                    {/* FORM */}
                    <div className="lg:col-span-7 py-16 lg:py-24 lg:pl-16">
                        <form data-testid={CONTACT.form} onSubmit={submit} className="space-y-10">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <label className="block">
                                        <span className="overline block mb-3">Your name</span>
                                        <input
                                            type="text"
                                            data-testid={CONTACT.name}
                                            value={form.name}
                                            onChange={update("name")}
                                            placeholder="Alex Morgan"
                                            className="input-line"
                                            required
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="overline block mb-3">Work email</span>
                                        <input
                                            type="email"
                                            data-testid={CONTACT.email}
                                            value={form.email}
                                            onChange={update("email")}
                                            placeholder="alex@yourfirm.com"
                                            className="input-line"
                                            required
                                        />
                                    </label>
                                </div>

                                <label className="block">
                                    <span className="overline block mb-3">Company</span>
                                    <input
                                        type="text"
                                        data-testid={CONTACT.company}
                                        value={form.company}
                                        onChange={update("company")}
                                        placeholder="Your brokerage or financial services firm"
                                        className="input-line"
                                    />
                                </label>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <label className="block">
                                        <span className="overline block mb-3">What are you scaling?</span>
                                        <select
                                            data-testid={CONTACT.service}
                                            value={form.service}
                                            onChange={update("service")}
                                            className="input-line appearance-none cursor-pointer"
                                        >
                                            <option value="" className="bg-brand-bg">Select a service</option>
                                            {SERVICES.map((s) => (
                                                <option key={s} value={s} className="bg-brand-bg">{s}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="overline block mb-3">Budget</span>
                                        <select
                                            data-testid={CONTACT.budget}
                                            value={form.budget}
                                            onChange={update("budget")}
                                            className="input-line appearance-none cursor-pointer"
                                        >
                                            <option value="" className="bg-brand-bg">Select a range</option>
                                            {BUDGETS.map((b) => (
                                                <option key={b} value={b} className="bg-brand-bg">{b}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
                                    <p className="text-xs text-brand-primary/70 max-w-sm">
                                        By submitting, you agree to be contacted by the Adx Fusion team. We don&apos;t share your data.
                                    </p>
                                    <button
                                        type="submit"
                                        data-testid={CONTACT.submit}
                                        disabled={loading}
                                        className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>Sending <Loader2 className="w-4 h-4 animate-spin" /></>
                                        ) : (
                                            <>Send brief <ArrowUpRight className="w-4 h-4" /></>
                                        )}
                                    </button>
                                </div>
                            </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
